import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { imgUrl } from "../api";
import { HERO_SLIDE_DEFAULTS, normalizeHeroSlides } from "../data/heroSlides";

export const COUTURE_3D_ITEMS = HERO_SLIDE_DEFAULTS.map((slide) => ({
  ...slide,
  src: slide.image,
}));

export default function Couture3DCarousel({ items = COUTURE_3D_ITEMS }) {
  const slides = useMemo(
    () =>
      normalizeHeroSlides(items).map((slide, index) => ({
        ...slide,
        src:
          slide.src ||
          slide.image ||
          HERO_SLIDE_DEFAULTS[index]?.image ||
          HERO_SLIDE_DEFAULTS[0].image,
      })),
    [items]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [manualPaused, setManualPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const touchStart = useRef(null);
  const isPaused = interactionPaused || manualPaused;

  useEffect(() => {
    if (activeIndex >= slides.length) setActiveIndex(0);
  }, [activeIndex, slides.length]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updatePreference);
      return () => mediaQuery.removeEventListener("change", updatePreference);
    }
    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updatePreference);
      return () => mediaQuery.removeListener(updatePreference);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const nextIndex = (activeIndex + 1) % slides.length;
    [slides[activeIndex], slides[nextIndex]].filter(Boolean).forEach((item) => {
      const image = new Image();
      const prefersMobile =
        typeof window.matchMedia === "function" && window.matchMedia("(max-width: 560px)").matches;
      image.src = imgUrl((prefersMobile && item.mobile_image) || item.src || item.image || item.fallbackSrc);
    });
  }, [activeIndex, slides]);

  const navigate = useCallback(
    (direction) => {
      setActiveIndex((current) => {
        const delta = direction === "next" ? 1 : -1;
        return (current + delta + slides.length) % slides.length;
      });
    },
    [slides.length]
  );

  useEffect(() => {
    if (isPaused || prefersReducedMotion || slides.length <= 1) return undefined;
    const timer = window.setTimeout(() => navigate("next"), 6500);
    return () => window.clearTimeout(timer);
  }, [activeIndex, isPaused, navigate, prefersReducedMotion, slides.length]);

  const activeItem = slides[activeIndex] || slides[0];
  const activeSrc = imgUrl(activeItem.src || activeItem.image || activeItem.fallbackSrc);
  const activeMobileSrc = activeItem.mobile_image ? imgUrl(activeItem.mobile_image) : null;
  const fallbackSrc = imgUrl(activeItem.fallbackSrc || COUTURE_3D_ITEMS[activeIndex % COUTURE_3D_ITEMS.length].src);
  return (
    <section
      className="hero-editorial-section"
      data-testid="hero-section"
      style={{ "--hero-tone": activeItem.bg || "#4a121e" }}
      onMouseEnter={() => setInteractionPaused(true)}
      onMouseLeave={() => setInteractionPaused(false)}
      onTouchStart={(event) => {
        const touch = event.touches?.[0];
        touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
        setInteractionPaused(true);
      }}
      onTouchEnd={(event) => {
        const touch = event.changedTouches?.[0];
        if (touchStart.current && touch) {
          const distanceX = touch.clientX - touchStart.current.x;
          const distanceY = touch.clientY - touchStart.current.y;
          if (Math.abs(distanceX) > 48 && Math.abs(distanceX) > Math.abs(distanceY) * 1.2) {
            navigate(distanceX < 0 ? "next" : "prev");
          }
        }
        touchStart.current = null;
        setInteractionPaused(false);
      }}
      onTouchCancel={() => {
        touchStart.current = null;
        setInteractionPaused(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") navigate("prev");
        if (event.key === "ArrowRight") navigate("next");
      }}
      tabIndex={0}
      aria-label="Featured couture collection"
    >
      <div className="couture-hero-media" key={`${activeItem.id || activeIndex}-${activeIndex}`}>
        <picture>
          {activeMobileSrc && <source media="(max-width: 560px)" srcSet={activeMobileSrc} />}
          <img
            src={activeSrc}
            alt={activeItem.title || "Lalita Soujanya couture"}
            loading={activeIndex === 0 ? "eager" : "lazy"}
            fetchPriority={activeIndex === 0 ? "high" : "auto"}
            decoding="async"
            onError={(event) => {
              const image = event.currentTarget;
              const mobileSource = image.parentElement?.querySelector("source");
              if (mobileSource && image.dataset.fallbackStage !== "desktop") {
                mobileSource.remove();
                image.dataset.fallbackStage = "desktop";
                image.src = activeSrc;
                return;
              }
              if (image.dataset.fallbackStage === "default") return;
              image.dataset.fallbackStage = "default";
              image.src = fallbackSrc;
            }}
          />
        </picture>
      </div>
      <div className="couture-hero-overlay" aria-hidden="true" />
      <div className="couture-hero-frame" aria-hidden="true" />

      <div className="couture-hero-content">
        <div className="couture-hero-copy" aria-live="polite">
          <div className="couture-hero-meta">
            <span>{activeItem.tag || "Lalita Soujanya / Hyderabad"}</span>
            <span>{String(activeIndex + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}</span>
          </div>
          <h1 data-testid="hero-title">{activeItem.title}</h1>
          {activeItem.subtitle && <p className="couture-hero-subtitle" data-testid="hero-subtitle">{activeItem.subtitle}</p>}
          {activeItem.description && <p className="couture-hero-description">{activeItem.description}</p>}
          <div className="couture-hero-actions">
            <Link to={activeItem.primary_cta_link || "/shop"} className="btn couture-hero-primary" data-testid="hero-shop-button">
              {activeItem.primary_cta_label || "Explore collection"}
              <ArrowUpRight size={15} />
            </Link>
            {activeItem.secondary_cta_label && (
              <Link to={activeItem.secondary_cta_link || "/customization"} className="btn couture-hero-secondary" data-testid="hero-explore-button">
                {activeItem.secondary_cta_label}
              </Link>
            )}
          </div>
        </div>

        <div className="couture-hero-controls">
          <div className="couture-hero-progress" aria-label="Choose a featured collection">
            {slides.map((item, index) => (
              <button
                type="button"
                key={item.id || index}
                className={index === activeIndex ? "is-active" : ""}
                onClick={() => setActiveIndex(index)}
                aria-label={`Show ${item.title || `collection ${index + 1}`}`}
                aria-current={index === activeIndex ? "true" : undefined}
              >
                <span />
              </button>
            ))}
          </div>
          <div className="couture-hero-arrows">
            <button
              type="button"
              onClick={(event) => {
                const isResuming = manualPaused;
                setManualPaused((paused) => !paused);
                if (isResuming) {
                  setInteractionPaused(false);
                  event.currentTarget.blur();
                }
              }}
              aria-label={manualPaused ? "Resume automatic slideshow" : "Pause automatic slideshow"}
              aria-pressed={manualPaused}
              title={manualPaused ? "Play slideshow" : "Pause slideshow"}
            >
              {manualPaused ? <Play size={17} /> : <Pause size={17} />}
            </button>
            <button type="button" onClick={() => navigate("prev")} aria-label="Previous collection">
              <ChevronLeft size={19} />
            </button>
            <button type="button" onClick={() => navigate("next")} aria-label="Next collection">
              <ChevronRight size={19} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
